import { Dropdown, type MenuElement, MenuItem } from "prosemirror-menu";
import type { Attrs, NodeType } from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { isHeadingNode, schema } from "../adapter/schema";
import { getSharedAttrs } from "../utils";

import { t } from "i18n/i18n";

/** Returns true if the selection spans multiple blocks with different types or heading levels. */
function selectionHasMultipleBlockLevels(state: EditorState): boolean {
  const { from, to } = state.selection;
  const keys = new Set<string>();
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isTextblock) return;
    const key = `${node.type.name}-${isHeadingNode(node) ? node.attrs.level : null}`;
    keys.add(key);
    if (keys.size > 1) return false;
  });
  return keys.size > 1;
}

/** Get the current block type label based on the editor state. */
function getCurrentBlockLabel(state: EditorState): string {
  if (selectionHasMultipleBlockLevels(state)) return t("worksheet.rich_text_editor.mixed");

  const { $from } = state.selection;
  const node = $from.parent;

  if (isHeadingNode(node)) {
    const { level } = node.attrs;
    return t("worksheet.rich_text_editor.heading", { level });
  }

  return t("worksheet.rich_text_editor.normal");
}

/** Create a MenuItem that changes the selected block(s) to the given node type, preserving shared attrs (align, indent) from each existing node. */
function blockTypeMenuItem(
  nodeType: NodeType,
  typeSpecificAttrs: Attrs | null,
  label: string,
  renderBtn: () => HTMLButtonElement,
): MenuItem {
  return new MenuItem({
    label,
    run(state, dispatch) {
      const { selection, doc, tr } = state;
      let changed = false;
      doc.nodesBetween(selection.from, selection.to, (node, pos) => {
        const targetAttrs = { ...getSharedAttrs(node), ...typeSpecificAttrs };
        if (!node.hasMarkup(nodeType, targetAttrs)) {
          tr.setBlockType(pos, pos + node.nodeSize, nodeType, targetAttrs);
          changed = true;
        }
        return false; // Never recurse into child nodes
      });
      if (!changed) return false;
      dispatch?.(tr.scrollIntoView());
      return true;
    },
    render: renderBtn,
  });
}

/**
 * Create a menu item for a heading level
 *
 * Each heading menu item displays text styled as it would appear in the document,
 * allowing users to preview the heading style before selecting it.
 *
 * @param level - The heading level (1-6)
 */
export function headingMenuItem(nodeType: NodeType, level: number): MenuItem {
  // Heading sizes should match what appears in the editor
  const fontSizeMap: Record<number, string> = {
    1: "2em",
    2: "1.5em",
    3: "1.17em",
    4: "1em",
    5: "0.83em",
    6: "0.67em",
  };
  const headingLabel = t("worksheet.rich_text_editor.heading", { level });

  return blockTypeMenuItem(nodeType, { level }, headingLabel, () => {
    const btn = document.createElement("button");
    btn.className = `block w-full text-left font-bold leading-normal ${level === 5 ? "uppercase" : "normal-case"}`;
    btn.style.fontSize = fontSizeMap[level] ?? "1em";
    btn.textContent = headingLabel;
    return btn;
  });
}

/** Create a menu item for "Normal" (paragraph) text style. */
export function normalMenuItem(nodeType: NodeType): MenuItem {
  const normalLabel = t("worksheet.rich_text_editor.normal");

  return blockTypeMenuItem(nodeType, null, normalLabel, () => {
    const btn = document.createElement("button");
    btn.className = "block w-full text-left font-normal leading-normal";
    btn.style.fontSize = "1em";
    btn.textContent = normalLabel;
    return btn;
  });
}

/** Custom dropdown that updates its label based on the current block type. */
export class DynamicTextStyleDropdown implements MenuElement {
  private dropdown: Dropdown;

  private labelEl: HTMLSpanElement | null = null;

  constructor() {
    const items: MenuElement[] = [];

    // Add heading levels 1-6 options
    for (let level = 1; level <= 6; level += 1) {
      items.push(headingMenuItem(schema.nodes.heading, level));
    }

    // Add "Normal" (paragraph) option
    items.push(normalMenuItem(schema.nodes.paragraph));

    // Create dropdown with empty label initially - we'll update it dynamically
    this.dropdown = new Dropdown(items, {
      label: "",
      title: t("worksheet.rich_text_editor.text_style"),
    });
  }

  render(view: EditorView) {
    const { dom, update } = this.dropdown.render(view);

    // Set up the dropdown label
    const dropdownBtn = dom.querySelector(".ProseMirror-menu-dropdown") as HTMLButtonElement;
    if (dropdownBtn) {
      const dropdownLabel = document.createElement("span");
      dropdownLabel.className = "text-style-dropdown-label";
      dropdownLabel.textContent = getCurrentBlockLabel(view.state);
      dropdownBtn.textContent = "";
      dropdownBtn.appendChild(dropdownLabel);
      this.labelEl = dropdownLabel;
    }

    // Wrap the update function in order to update label text when state changes
    const wrappedUpdate = (state: EditorState) => {
      const result = update(state);
      if (this.labelEl) {
        this.labelEl.textContent = getCurrentBlockLabel(state);
      }
      return result;
    };

    return { dom, update: wrappedUpdate, focusable: dropdownBtn };
  }
}
